import { useEffect, useState } from 'react';
import { fetchSearchFacets } from '../data/api/search';
import { filterKey, type FilterState } from '../utils/search-filter-state';
import type { SearchFacets } from '../schemas/api/search/facets';

/** Wait this long after the last change before asking, so a run of taps is one request. */
export const FACETS_DEBOUNCE_MS = 250;

/**
 * Filter options and counts for the drawer, refetched as the shopper changes
 * the draft (each count is for the listings the other choices leave). Only
 * while `enabled`: a closed drawer asks nothing. An answer for an older draft
 * is dropped.
 */
export function useSearchFacets(
  scope: { categoryId?: number; categoryGroupId?: number },
  filters: FilterState,
  enabled: boolean,
): { facets: SearchFacets | null; loading: boolean } {
  const [facets, setFacets] = useState<SearchFacets | null>(null);
  const [loading, setLoading] = useState(false);
  const key = `${scope.categoryId ?? 0}:${scope.categoryGroupId ?? 0}:${filterKey(filters)}`;

  useEffect(() => {
    if (!enabled) return undefined;
    let current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- marks the request this effect is about to make as in flight
    setLoading(true);
    const timer = setTimeout(() => {
      void fetchSearchFacets(scope, filters).then((result) => {
        if (!current) return;
        setFacets(result);
        setLoading(false);
      });
    }, FACETS_DEBOUNCE_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
    // `key` is the identity of scope + filters; the objects themselves change every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return { facets, loading };
}
